<template>
	<div v-click-outside="handleClickOutside" class="search-bar-container d-flex justify-end" :class="{ expanded: searchVisible }">
		<BaseSearchField
			v-if="display.lgAndUp.value || searchVisible"
			ref="searchInput"
			v-model="searchTerm"
			v-test="'global-search'"
			v-track="'global-search'"
			id="search-bar-input"
			autocomplete="off"
			placeholder="Search everywhere"
			class="global-search"
			type="search"
			@keydown="handleSearchKeyDown"
			@blur="sanitize"
			@focus="sanitize"
			@input="onSearchInput"
			@input.native="handleSearchInput"
			@keyup.enter="search"
			@click:clear="clearSearch"
			@paste="onSearchPaste">
			<!-- LSP-20609: the funnel used to be an `append-inner-icon`, which renders as decoration
			     sitting inside the field — indistinguishable from the search glyph, with no hover
			     affordance and no accessible name. It is a separate action, so it is drawn as a real
			     button, separated from the input by a rule and labelled by a tooltip. -->
			<template #append-inner>
				<v-tooltip location="bottom" text="Search and filter everything" open-delay="200">
					<template #activator="{ props: tooltipProps }">
						<v-btn
							v-bind="tooltipProps"
							v-test="'global-search-filter'"
							v-track="'global-search-filter'"
							class="global-search-filter"
							icon="icon-filter-solid"
							variant="text"
							density="comfortable"
							size="small"
							aria-label="Search and filter everything"
							@click.stop="search" />
					</template>
				</v-tooltip>
			</template>
		</BaseSearchField>
		<v-btn v-else icon="icon-search-magnifying-glass" density="compact" @click="focusSearch"></v-btn>
	</div>
</template>

<script lang="ts" setup>
	import { isSearchTooLong } from "@/helpers/searchLimit";
	import BaseSearchField from "@/components/formfields/BaseSearchField.vue";
	import { getCurrentInstance, nextTick, ref, watch } from "vue";
	import router from "@/router";
	import { useToast } from "vue-toastification";
	import {
		handleSearchKeyDown,
		handleSearchInput,
		handleSearchPaste,
		cleanSearchTerm,
		dismissSoftKeyboard,
		isShortGlobalSearchTerm,
		SHORT_GLOBAL_SEARCH_TERM_MESSAGE
	} from "@/helpers/filters";
	import clickOutside from "@/directives/clickOutside";
	import { calculateTimeout } from "@/helpers/utilities";
	import { useDisplay } from "vuetify";
	import type { VTextField } from "vuetify/components";

	const display = useDisplay();
	const searchTerm = ref("");
	const searchVisible = ref(false);
	const instance = getCurrentInstance();
	const toast = useToast();
	const searchInput = ref<InstanceType<typeof VTextField> | null>(null);

	// Define the click outside directive for this component
	const vClickOutside = clickOutside;

	function clearSearch() {
		searchTerm.value = "";
	}

	watch(
		() => searchTerm.value,
		(newVal) => {
			const cleaned = cleanSearchTerm(newVal);
			if (newVal !== cleaned) {
				searchTerm.value = cleaned;
			}

			nextTick(() => {
				if (searchInput.value) {
					const input = (searchInput.value as any).$el.querySelector("input");

					if (input) {
						input.value = cleaned;
					}
				}
			});
		}
	);

	async function onSearchPaste(event: ClipboardEvent) {
		if (!handleSearchPaste(event)) {
			const message = "Pasted text contained invalid characters. Please remove any special characters and try again.";
			toast.error(message, { timeout: calculateTimeout(message) });
		}
	}

	function onSearchInput() {
		sanitize();
	}

	function sanitize() {
		const cleaned = cleanSearchTerm(searchTerm.value);
		searchTerm.value = cleaned;

		nextTick(() => {
			if (searchInput.value) {
				const input = (searchInput.value as any).$el.querySelector("input");

				if (input) {
					input.value = cleaned;
				}
			}
		});
	}

	function search(event?: Event) {
		if (isSearchTooLong(searchTerm.value)) return;
		// LSP-15898: blur synchronously BEFORE the router runs — router.go(0)
		// below tears down the DOM, so any deferred blur would land on a
		// detached node and iOS would keep the soft keyboard up.
		dismissSoftKeyboard(event, searchInput.value as any);

		// LSP-20656: the API rejects 1-2 character text terms (no trigram, whole-org scan). Say why here
		// and leave the box as typed rather than navigating into a request that can only 400.
		if (isShortGlobalSearchTerm(searchTerm.value)) {
			// LSP-20846: guidance, not a failure — warning (yellow), never error (red).
			toast.warning(SHORT_GLOBAL_SEARCH_TERM_MESSAGE, { timeout: calculateTimeout(SHORT_GLOBAL_SEARCH_TERM_MESSAGE) });
			return;
		}

		if (router.currentRoute.value.name === "search") {
			router.push({ path: "/search", query: { searchTerm: searchTerm.value.trim() } }).then(() => {
				router.go(0); // Refresh the page to update the search query
			});
		} else {
			if (searchTerm.value) {
				router.push({ path: "/search", query: { searchTerm: searchTerm.value.trim() } });
				searchTerm.value = "";
			} else {
				router.push({ path: "/search" });
			}
		}

		searchVisible.value = false;
	}

	function focusSearch() {
		//if (router.currentRoute.name !== "search") {
		searchVisible.value = true;
		// } else {
		// 	eventBus.$emit("focus-search");
		// }
	}

	function handleClickOutside() {
		// Only hide search bar on mobile when search is visible
		if (display.mdAndDown.value && searchVisible.value) {
			searchVisible.value = false;
			searchTerm.value = "";
		}
	}
</script>

<style scoped lang="scss">
	.search-bar-container {
		min-width: 0;
		@media #{map.get($display-breakpoints, 'sm-and-down')} {
			display: flex;
		}
	}

	.v-input.global-search {
		font-size: 0.9rem !important;
		border-radius: 28px !important;
		flex: 0 1 auto;
		width: 100%;
		min-width: 0;

		@media #{map.get($display-breakpoints, 'sm-and-down')} {
			flex: 1;
		}
	}

	.global-search.v-input.v-input--hide-details {
		margin-bottom: 0;
	}

	/* LSP-20609: a rule and a hover state are what separate "this is an action you can click" from
	   "this is an icon decorating the field". Without them the funnel read as part of the input. */
	.v-text-field.global-search :deep(.global-search-filter) {
		border-left: 1px solid rgba(0, 0, 0, 0.18);
		border-radius: 0;
		margin-left: 4px;
		padding-left: 4px;
		cursor: pointer;
	}

	.v-text-field.global-search :deep(.global-search-filter:hover),
	.v-text-field.global-search :deep(.global-search-filter:focus-visible) {
		color: rgb(var(--v-theme-primary));
	}

	// @media (max-width: 600px) {
	// 	.search-bar-container {
	// 		display: flex;
	// 	}

	// 	.v-input.global-search {
	// 		flex: 1;
	// 	}
	// }
</style>
